import {
  DialogActions,
  DialogContent,
  Stack,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useFetchPriceControlItemsEvents } from '@retail/price-control/data-access';
import { PriceControlAction, PriceControlValidate } from '@retail/price-control/types';
import { EventAction, HandleItemsEventActionTable } from '@retail/price-control/components';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { ContainedButton, OutlinedButton } from '@shared/components';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';

interface Props {
  itemIdsToHandle: number[];
  includeVat: boolean;
  handleItemEventAction: (action: PriceControlAction) => void;
  onClose: () => void;
}

export function HandleItemsEventAction({
  itemIdsToHandle,
  includeVat,
  handleItemEventAction,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();
  const { assortmentId, appTenant } = useSelectedOrgUnit();
  const [selectedAction, setSelectedAction] = useState<PriceControlAction>('DO_NOTHING');
  const [currentItemIdsToHandle, setItemIdsToHandle] = useState<number[]>(itemIdsToHandle);

  const { data: itemsDTO = [], isLoading } = useFetchPriceControlItemsEvents({
    assortmentId,
    memberEvent: { action: selectedAction, memberEventIds: currentItemIdsToHandle },
    suspense: true,
  });

  const items = useMemo(() => itemsDTO.map(PriceControlValidate.fromDto), [itemsDTO]);

  const validItems = useMemo(
    () =>
      items
        .filter((item) => item.isValid)
        .sort((a, b) => (b.isValid ? 1 : 0) - (a.isValid ? 1 : 0)),
    [items]
  );

  return (
    <>
      <DialogContent className="padding-0">
        <Stack height="100%">
          <Stack px={3} pb={3} gap={1}>
            <Typography variant="subtitle1">
              {t('myAssortment.priceControl.selectAction')}:
            </Typography>
            <ToggleButtonGroup
              sx={{
                width: '100%',
                gap: 1,
                bgcolor: 'background.paper',
                '& .MuiButtonBase-root.MuiToggleButton-root': {
                  '&::before, &::after': {
                    display: 'none !important',
                  },
                },
              }}
              exclusive
              value={selectedAction}
              disabled={isLoading}
              onChange={(_, value) => (!value ? null : setSelectedAction(value))}
            >
              <StyledToggleButton value={'DO_NOTHING'}>
                <EventAction action="DO_NOTHING" />
              </StyledToggleButton>
              <StyledToggleButton value={'KEEP_OLD_COVERAGE'}>
                <EventAction action="KEEP_OLD_COVERAGE" />
              </StyledToggleButton>
              <StyledToggleButton value={'KEEP_OLD_PRICE'}>
                <EventAction action="KEEP_OLD_PRICE" />
              </StyledToggleButton>
              <StyledToggleButton value={'FREEZE_OLD_PRICE'}>
                <EventAction action="FREEZE_OLD_PRICE" />
              </StyledToggleButton>
              <StyledToggleButton value={'SPLIT_DIFFERENCE'}>
                <EventAction action="SPLIT_DIFFERENCE" />
              </StyledToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <HandleItemsEventActionTable
            externalItemSource={appTenant.externalItemSource}
            items={items}
            isLoading={isLoading}
            includeVat={includeVat}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <OutlinedButton size="small" onClick={onClose}>
          {t('myAssortment.cancel')}
        </OutlinedButton>
        <ContainedButton
          size="small"
          onClick={() => {
            handleItemEventAction(selectedAction);
            validItems < items
              ? setItemIdsToHandle(
                  items.filter((item) => !item.isValid).map((item) => item.memberEventId)
                )
              : onClose();
          }}
        >
          {validItems < items
            ? t('myAssortment.priceControl.handleNrValidItems', { nr: validItems.length })
            : t('myAssortment.actions.handleItems')}
        </ContainedButton>
      </DialogActions>
    </>
  );
}

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  border: 'none',
  flex: 1,
  outline: `2px solid ${theme.vars.palette.divider} !important`,
}));
